import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RestrictService, InstanceRights } from '../Common/Permissions/restrict.service';
import { WsTypeaheadDirective } from '../UI-Lib/Typeahead/typeahead.directive';
import { Team } from './team.model';
import { TeamService } from './team.service';
import * as _ from 'lodash';

export interface CostCenterTeam extends Team {
    costCenterString: string;
}

@Component({
    selector: 'team-picker',
    template: `
        @if (editMode) {
            <input
                type="text"
                [(ngModel)]="typeaheadString"
                [wsTypeahead]="teams"
                [typeaheadScrollable]="true"
                [typeaheadOptionsInScrollableView]="10"
                [placeholder]="placeHolder"
                (typeaheadOnSelect)="itemSelected($event.item)"
                [typeaheadMinLength]="2"
                container="body"
                [typeaheadOptionField]="typeaheadField"
                [typeaheadOptionsLimit]="20"
                [disabled]="dataLoading || !hasEditPermission"
                class="form-control"
            />
        }
        @if (team && !editMode) {
            <span>
                @if (useCostCenter) {
                    <span>{{ team.costCenterString }}</span>
                }
                @if (!useCostCenter) {
                    <span>{{ team.name }}</span>
                }
            </span>
        }
    `,
    imports: [FormsModule, WsTypeaheadDirective],
})
export class TeamPickerComponent implements OnInit {
    constructor(
        private readonly _teamsService: TeamService,
        private readonly _restrictService: RestrictService,
    ) {}

    @Input() useCostCenter: boolean;
    @Input() team: CostCenterTeam;
    @Input() teamId: number;
    @Input() costCenter: string;
    @Input() costCenterTeamId: number;
    @Input() editMode = true;
    @Input() canEdit: boolean = false;
    @Output() teamChange: EventEmitter<CostCenterTeam> = new EventEmitter<CostCenterTeam>();
    @Output() teamIdChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() costCenterChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() costCenterTeamIdChange: EventEmitter<number> = new EventEmitter<number>();

    hasEditPermission: boolean = false;
    dataLoading: boolean = false;
    teams: CostCenterTeam[];
    placeHolder: string = '';
    typeaheadField: string = '';
    typeaheadString: string = '';

    async ngOnInit(): Promise<void> {
        try {
            this.dataLoading = true;
            const teams = (await this._teamsService.getAllTeams(false)) as CostCenterTeam[];
            this.teams = _.sortBy(teams, 'name');

            if (this.useCostCenter) {
                this.teams = _.chain(this.teams)
                    .filter('costCenter')
                    .map((x) => {
                        x.costCenterString = `${x.costCenter || ''} (${x.name})`;
                        return x;
                    })
                    .value();

                this.placeHolder = 'Search Cost Center';
                this.typeaheadField = 'costCenterString';
                this.typeaheadString = this.costCenter;

                if (this.costCenterTeamId) {
                    this.team = _.find(this.teams, { teamID: this.costCenterTeamId });
                } else if (this.costCenter) {
                    this.team = _.find(this.teams, { costCenter: this.costCenter });
                    this.costCenterTeamId = this.team.teamID;
                    this.costCenterTeamIdChange.emit(this.costCenterTeamId);
                }
                this.typeaheadString = this.team?.costCenterString;

                this.hasEditPermission = true;
            } else {
                this.placeHolder = 'Search Teams';
                this.typeaheadField = 'name';

                if (this.team) {
                    this.typeaheadString = this.team.name;
                }

                this.hasEditPermission =
                    this._restrictService.hasInstanceRight(InstanceRights.TEAMSETUPSEDIT) || this.canEdit;
            }
        } finally {
            this.dataLoading = false;
        }
    }

    itemSelected(team: CostCenterTeam) {
        this.team = team;
        this.costCenter = this.team.costCenter;
        this.typeaheadString = this.useCostCenter ? this.team.costCenterString : this.team.name;

        this.teamChange.emit(team);
        this.teamIdChange.emit(team.teamID);
        this.costCenterChange.emit(this.team.costCenter);
        this.costCenterTeamIdChange.emit(this.team.teamID);
    }
}
